// AuthContext.js
import React, { createContext, useContext, useState, useEffect  } from 'react';
import axios from 'axios';

// Create a context for authentication
const AuthContext = createContext();

// Custom hook to use the AuthContext
export const useAuth = () => useContext(AuthContext);

// AuthProvider component to wrap the app and provide auth state
export const AuthProvider = ({ children }) => {
  const roboman_api = process.env.REACT_APP_ROBOMAN_API;
  const clearAuthStorage = () => {
    [
      'isLoggedIn',
      'access_token',
      'request_user_id',
      'user_id',
      'user_data',
      'billing_plan',
      'has_smtp_integration',
    ].forEach((key) => localStorage.removeItem(key));
  };

  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // Initialize state from localStorage
    return localStorage.getItem('isLoggedIn') === 'true';
  });

  const [billingPlan, setBillingPlan] = useState(() => {
    return localStorage.getItem('billing_plan') || null;
  });

  const [hasSmtpIntegration, setHasSmtpIntegration] = useState(() => {
    return localStorage.getItem('has_smtp_integration') === 'true';
  });

  const [loading, setLoading] = useState(true);

  const login = () => {
    setIsLoggedIn(true);
    localStorage.setItem('isLoggedIn', 'true');
  };

  const logout = () => {
    setIsLoggedIn(false);
    setBillingPlan(null);
    setHasSmtpIntegration(false);
    clearAuthStorage();
  };

  const checkTokenValidity = async () => {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      setIsLoggedIn(false);
      clearAuthStorage();
      setLoading(false);
      return;
    }

    try {
      // Make the API call to validate the token
      const response = await axios.get(`${roboman_api}/users/me`, {
        headers: {
          'Authorization': accessToken,
          'accept': 'application/json',
        },
      });

      // If the response is successful, stay logged in
      if (response.status === 200) {
        setIsLoggedIn(true);
        const plan = response.data?.user_info?.billing_plan || null;
        setBillingPlan(plan);
        if (plan) {
          localStorage.setItem('billing_plan', plan);
        } else {
          localStorage.removeItem('billing_plan');
        }
        const smtpList = response.data?.smtp_integrations ?? [];
        const hasSmtp = smtpList.length > 0;
        setHasSmtpIntegration(hasSmtp);
        localStorage.setItem('has_smtp_integration', String(hasSmtp));
      } else if (response.status === 401 || response.status === 403) {
        logout();
      }
    } catch (error) {
      const statusCode = error?.response?.status;

      if (statusCode === 401 || statusCode === 403) {
        console.error('Token expired or invalid:', error);
        logout();
      } else {
        console.error('Token validation failed without an auth error:', error);
      }
    } finally {
      setLoading(false); // Stop loading after the check
    }
  };

  useEffect(() => {
    // Check the token validity when the app first loads
    checkTokenValidity();
  }, []);

  return (
    <AuthContext.Provider value={{ isLoggedIn, login, logout, billingPlan, hasSmtpIntegration }}>
      {children}
    </AuthContext.Provider>
  );
};